import { gzipDecompress } from './documentGzip.ts'
import { getMediaBytes, bytesToObjectUrl } from './mediaCache.ts'
import { escapeHtml } from '../ui/dom.ts'

export type DocPreviewKind = 'pdf' | 'text' | 'none'

const plainRam = new Map<string, Uint8Array>()

export function docPreviewKind(mime: string, fileName: string): DocPreviewKind {
  const m = (mime || '').toLowerCase()
  const n = (fileName || '').toLowerCase()
  if (m === 'application/pdf' || n.endsWith('.pdf')) return 'pdf'
  if (m.startsWith('text/') || m === 'application/json' || n.endsWith('.txt')) return 'text'
  return 'none'
}

export function clearDocumentPreviewCache(): void {
  plainRam.clear()
}

export async function getPlainDocumentBytes(
  contactId: string,
  messageId: string,
  gzipped?: boolean,
): Promise<Uint8Array | null> {
  const key = `${contactId}:${messageId}`
  const cached = plainRam.get(key)
  if (cached) return cached
  const wire = await getMediaBytes(contactId, messageId)
  if (!wire) return null
  try {
    const plain = gzipped ? await gzipDecompress(wire) : wire
    plainRam.set(key, plain)
    return plain
  } catch {
    return null
  }
}

export function buildDocumentPreviewMarkup(
  bytes: Uint8Array,
  mime: string,
  fileName: string,
  urlStore: Map<string, string>,
  urlKey: string,
): string {
  const kind = docPreviewKind(mime, fileName)
  if (kind === 'pdf') {
    let url = urlStore.get(urlKey)
    if (!url) {
      url = bytesToObjectUrl(bytes, 'application/pdf')
      urlStore.set(urlKey, url)
    }
    return `<div class="media-doc-preview"><iframe class="media-doc-preview-iframe" src="${url}" title="Anteprima PDF"></iframe></div>`
  }
  if (kind === 'text') {
    let text = ''
    try {
      text = new TextDecoder('utf-8', { fatal: false }).decode(bytes).slice(0, 5000)
    } catch {
      text = '(testo non decodificabile)'
    }
    return `<div class="media-doc-preview"><pre class="media-doc-preview-text">${escapeHtml(text)}</pre></div>`
  }
  return ''
}

export function buildSendPreviewMarkup(
  file: File,
  urlStore: Map<string, string>,
  urlKey: string,
): string {
  const kind = docPreviewKind(file.type, file.name)
  if (kind === 'pdf') {
    let url = urlStore.get(urlKey)
    if (!url) {
      url = URL.createObjectURL(file)
      urlStore.set(urlKey, url)
    }
    return `<iframe class="doc-preview-iframe" src="${url}" title="Anteprima PDF"></iframe>`
  }
  if (kind === 'text') {
    return `<p class="doc-preview-loading">Caricamento anteprima…</p>`
  }
  return `<p class="doc-preview-fallback">Anteprima non disponibile per questo formato.<br/>Il file verrà inviato cifrato.</p>`
}

export async function buildSendTextPreview(file: File): Promise<string> {
  try {
    const text = (await file.text()).slice(0, 8000)
    return `<pre class="doc-preview-text">${escapeHtml(text)}</pre>`
  } catch {
    return `<p class="doc-preview-fallback">Impossibile leggere l'anteprima testo.</p>`
  }
}
